import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { Magnetic, EASE } from "../lib/motion";
import { NAV } from "../mock";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [shrink, setShrink] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setShrink(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
        className="fixed top-4 inset-x-0 mx-auto z-[100] w-[calc(100%-2rem)] max-w-[1200px]"
      >
        <div className={`flex items-center justify-between rounded-full border border-white/10 bg-[#06271D]/85 backdrop-blur-xl shadow-[0_10px_40px_rgba(4,20,14,0.35)] transition-all duration-300 ${shrink ? "px-4 py-2" : "px-5 py-2.5"}`}>
          <Logo />
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} data-cursor="hover">
                {({ isActive }) => (
                  <span className={`relative px-3.5 py-2 font-body text-[13.5px] font-semibold transition-colors ${isActive ? "text-[var(--amber-light)]" : "text-[#F4ECE0]/85 hover:text-[var(--amber-light)]"}`}>
                    {item.label}
                    {isActive && <motion.span layoutId="nav-dot" className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1.5 h-1.5 rounded-full sunset-fill" />}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Magnetic strength={0.5} className="hidden sm:block">
              <button onClick={() => navigate("/booking")} className="btn-shine sunset-fill text-white rounded-full px-5 py-2 font-body font-bold text-[13.5px]">Book Now</button>
            </Magnetic>
            <button className="md:hidden p-1.5" onClick={() => setOpen((v) => !v)} aria-label="Menu">
              {open ? <X size={26} className="text-[#F4ECE0]" /> : <Menu size={26} className="text-[#F4ECE0]" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, clipPath: "circle(0% at 90% 6%)" }}
            animate={{ opacity: 1, clipPath: "circle(150% at 90% 6%)" }}
            exit={{ opacity: 0, clipPath: "circle(0% at 90% 6%)" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="fixed inset-0 z-[99] md:hidden flex flex-col items-center justify-center gap-2"
            style={{ background: "linear-gradient(180deg,#06271D,#0B3D2E)" }}
          >
            {NAV.map((item, i) => (
              <motion.div key={item.to} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }}>
                <NavLink to={item.to} className="font-display font-bold text-4xl text-[#F4ECE0] hover:text-[var(--amber)] transition-colors">{item.label}</NavLink>
              </motion.div>
            ))}
            <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} onClick={() => { setOpen(false); navigate("/booking"); }} className="sunset-fill text-white rounded-full px-8 py-3 font-body font-bold mt-6">Book Now</motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
